import { deleteCookie } from 'cookies-next'
import { useTranslation } from 'next-i18next'
import { Text } from 'react-native'

import { APP_ENV } from '@/config/env'
import { getFlowSessionCookieName } from '@/server/routers/flows/utils/cookies'
import { reportError } from '@/services/errorReporting'
import type { TRPCClientErrorLike } from '@trpc/client'
import Button from '@ui-kit/components/Button'
import { SomethingWentWrong } from '@ui-kit/components/SomethingWentWrong'
import Card from '@ui-kit/components/cards/Card'
import Stack from '@ui-kit/components/layoutComponents/Stack'
import { body1, caption, h2, h4, h5 } from '@ui-kit/core/typography'
import type { Color } from '@ui-kit/types'

type TRPCError = TRPCClientErrorLike<any>

const HTTP_ERROR_CODES = {
  InternalServerError: 500,
}

interface Props {
  error: TRPCError | Error | null
  flowName: string
}

type InfoBoxProps = {
  title?: string
  content: string
  bgColor?: Color
}

const InfoBox = ({ title, content, bgColor }: InfoBoxProps) => {
  return (
    <Card bgColor={bgColor ?? 'uiGreyLight'}>
      <Stack space="xxsmall">
        {title ? (
          <Text className={h5()}>
            <span className={body1()} dangerouslySetInnerHTML={{ __html: title }} />
          </Text>
        ) : null}
        <Text className={caption({ emphasis: 'medium' })}>
          <span dangerouslySetInnerHTML={{ __html: content }} />
        </Text>
      </Stack>
    </Card>
  )
}

export const SomethingWentWrongFlow = ({ error, flowName }: Props) => {
  const { t } = useTranslation()

  if (error === null) {
    return null
  }

  function restart() {
    deleteCookie(getFlowSessionCookieName(flowName))
    void window.location.reload()
  }

  function retry() {
    void window.location.reload()
  }

  const getErrorContext = (err: TRPCError | Error) => {
    const baseErrorContext = {
      code: (error as TRPCError)?.data?.httpStatus || HTTP_ERROR_CODES.InternalServerError,
      name: (error as Error).name || (error as TRPCError)?.data?.code || 'Internal server error',
      message: err.message,
    }

    reportError(err, {
      ...baseErrorContext,
      flowName,
    })

    if ('code' in err) {
      return {
        ...baseErrorContext,
        code: err.code,
      }
    }

    return baseErrorContext
  }

  const { code, name, message } = getErrorContext(error)

  return (
    <SomethingWentWrong
      buttons={
        <>
          <Button
            type="outlined"
            variantColor="black"
            uppercase
            onPress={() => restart()}
            label={t('common:errors.crash.buttons.startOver')}
          />
          <Button
            variantColor="black"
            uppercase
            onPress={() => retry()}
            label={t('common:errors.crash.buttons.tryAgain')}
          />
        </>
      }
    >
      <Text className={h2({ type: 'primary', color: 'brandDark', align: 'center' })}>
        {t('common:errors.crash.title')}
      </Text>
      <Text className={h4({ color: 'uiDark', align: 'center' })}>
        {t('common:errors.crash.description')}
      </Text>
      <Text className={h4({ color: 'uiDark', align: 'center' })}>
        {t('common:errors.crash.errorCode', { code })}
      </Text>
      {APP_ENV !== 'production' && <InfoBox content={message} title={name} />}
    </SomethingWentWrong>
  )
}
